import React, { useState, useEffect, useCallback } from "react";
import { getAllProducts, getAllCategories } from "../../actions/ProductAction";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Pagination from "../layout/Pagination";
import ProductTable from "./ProductTable";
import { useBrands } from "../../utils/useBrand";
import Loading from "../layout/Loading";
import DropdownBox from "../layout/DropdownBox";

const ProductsOverview = () => {

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [brand, setBrand] = useState("");
   const [category, setCategory] = useState("");

  const {
    brands,
    loading: brandsLoading,
    loadMore
  } = useBrands(category);
  const brandList = [{ name: "All", id: "" }, ...brands];
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      const response = await getAllCategories();
      if (response?.success && Array.isArray(response.data?.categories)) {
        const list = [
          { name: "All", value: "" },
          ...response.data.categories.map((c) => ({ name: c, value: c }))
        ];
        setCategories(list);
      }
    };
    fetchCategories();
  }, []);

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleBrandChange = useCallback((e) => {
    setBrand(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleCategoryChange = useCallback((e) => {
    setCategory(e.target.value);
    setCurrentPage(1);
  }, []);

  const fetchAllProducts = useCallback(async () => {
    setIsLoading(true);
    const pagination = {
      page: currentPage,
      limit: itemsPerPage
    };

    try {
      const response = await getAllProducts(
        pagination,
        brand,
        searchTerm,
        category
      );

      if (response.success) {
        setProducts(response.data.products); 
        setTotalPages(response.data.totalPages); 
        setTotalCount(response.data.totalProducts); 
      } else {
        toast.error(response?.message || "Failed to fetch products.");
      }
    } catch (error) {
      toast.error(
        "Something went wrong while fetching products. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, brand, category]);

  useEffect(() => {
    fetchAllProducts();
  }, [fetchAllProducts]);

  const dropdownBaseStyle =
    "w-full md:w-[300px] h-[48px] rounded border border-gray-300 bg-white px-4 py-4 text-[14px] font-['Rubik'] focus:outline-none focus:ring-0";

  return (
    <div className="px-4 sm:px-10">
      {/* Filter/Search Row: SearchBar left, dropdowns right, no outer box, with gap */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-6 w-full">
        {/* Dropdowns (left) */}
        <div className="flex flex-row gap-4 w-full md:w-auto md:justify-start items-center mb-2 md:mb-0">
          <div className="flex flex-col gap-1 w-full md:w-auto">
            <label className="primary_label">Category</label>
            <DropdownBox
              optionList={categories}
              styleName={`${dropdownBaseStyle} `}
              value={category}
              displayKey="name"
              valueKey="value"
              placeholder="Select Category"
              compactIcon={true}
              isObjectData={true}
              handleChange={(selectedValue) =>
                handleCategoryChange({
                  target: { name: "category", value: selectedValue }
                })
              }
              emptyStringName="ALL"
              searchable={true}
            />
          </div>

          <div className="flex flex-col gap-1 w-full md:w-auto">
            <label className="primary_label">Brand</label>
            <DropdownBox
              optionList={brandList}
              styleName={`${dropdownBaseStyle} `}
              value={brand}
              displayKey="name"
              valueKey="id"
              placeholder="Select Brand"
              compactIcon={true}
              isObjectData={true}
              handleChange={(selectedValue) =>
                handleBrandChange({
                  target: { name: "brand", value: selectedValue }
                })
              }
              emptyStringName="ALL"
              searchable={true}
              onMenuScroll={loadMore}
              loading={brandsLoading}
            />
          </div>
        </div>
      </div>
      {/* ...existing code... */}
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <ProductTable
            products={products}
            setProducts={setProducts}
            onRefetch={fetchAllProducts}
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={[10, 20, 50, 100, 200]}
          />
        </>
      )}
    </div>
  );
};

export default ProductsOverview;
